/**
 * All Products page motion:
 * - fades product images in once decoded
 * - shows a loading state while filters/sort re-render the grid
 * - replays the card entrance animation after a refresh
 */

const SELECTOR_CONTAINER = '.product-grid-container';
const SELECTOR_IMAGE = '.card-gallery img';

const LOADING_DELAY = 180;
const MIN_LOADING_TIME = 400;
const LOADING_TIMEOUT = 8000;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

let loadingDelayId;
let loadingTimeoutId;
let loadingStartedAt = 0;
let lastContainer = null;

const getContainer = () => document.querySelector(SELECTOR_CONTAINER);

function startLoading() {
  clearTimeout(loadingDelayId);
  clearTimeout(loadingTimeoutId);

  loadingDelayId = setTimeout(() => {
    const container = getContainer();
    if (!container) return;

    loadingStartedAt = Date.now();
    container.setAttribute('data-pk-loading', 'true');
    loadingTimeoutId = setTimeout(stopLoading, LOADING_TIMEOUT);
  }, LOADING_DELAY);
}

function stopLoading() {
  clearTimeout(loadingDelayId);
  clearTimeout(loadingTimeoutId);

  const container = getContainer() ?? lastContainer;
  if (!container?.hasAttribute('data-pk-loading')) return;

  const remaining = Math.max(0, MIN_LOADING_TIME - (Date.now() - loadingStartedAt));
  setTimeout(() => {
    const current = getContainer() ?? lastContainer;
    current?.removeAttribute('data-pk-loading');
    replayEntrance(current);
  }, remaining);
}

function replayEntrance(container) {
  if (!container || reducedMotion.matches) return;

  container.classList.add('pk-replay');
  void container.offsetWidth;
  container.classList.remove('pk-replay');
}

function fadeInImage(image) {
  if (!(image instanceof HTMLImageElement) || image.classList.contains('pk-fade-img')) return;

  if (image.complete) {
    image.classList.add('pk-fade-img', 'pk-img-loaded');
    return;
  }

  image.classList.add('pk-fade-img');

  const reveal = () => image.classList.add('pk-img-loaded');
  image.addEventListener('load', reveal, { once: true });
  image.addEventListener('error', reveal, { once: true });
}

function scanImages(root) {
  if (!root || !(root instanceof Element)) return;
  if (root.matches(SELECTOR_IMAGE)) fadeInImage(root);
  root.querySelectorAll?.(SELECTOR_IMAGE).forEach(fadeInImage);
}

function isFilterInteraction(target) {
  if (!(target instanceof Element)) return false;
  if (target.closest('.column-options')) return false;
  return Boolean(target.closest('facets-form-component, sorting-filter-component'));
}

document.addEventListener(
  'change',
  (event) => {
    if (isFilterInteraction(event.target)) startLoading();
  },
  true,
);

document.addEventListener(
  'submit',
  (event) => {
    if (event.target instanceof Element && event.target.closest('facets-form-component')) startLoading();
  },
  true,
);

document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href*="page="]') : null;
    if (link && link.closest(SELECTOR_CONTAINER)) startLoading();
  },
  true,
);

function watchContainer() {
  const container = getContainer();
  if (!container) return;
  lastContainer = container;

  new MutationObserver((records) => {
    const gridChanged = records.some(
      (record) =>
        record.addedNodes.length > 0 ||
        record.removedNodes.length > 0 ||
        record.target.closest?.('.product-grid') != null,
    );

    if (gridChanged) {
      stopLoading();
      records.forEach((record) => record.addedNodes.forEach(scanImages));
    }
  }).observe(container, { childList: true, subtree: true });
}

function init() {
  scanImages(document);
  watchContainer();

  new MutationObserver(() => {
    const container = getContainer();
    if (container && container !== lastContainer) watchContainer();
  }).observe(document.querySelector('main') ?? document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
